import React, { memo } from "react";
import {
  Badge,
  Box,
  Circle,
  Flex,
  HStack,
  Text,
  Tooltip,
  VStack,
  useBreakpointValue,
  useColorModeValue,
  usePrefersReducedMotion,
} from "@chakra-ui/react";
import { keyframes } from "@emotion/react";
import { BsCheck, BsLightningChargeFill } from "react-icons/bs";

const arrowFlowHorizontal = keyframes`
  from { background-position-x: 0; }
  to { background-position-x: 24px; }
`;

const arrowFlowVertical = keyframes`
  from { background-position-y: 0; }
  to { background-position-y: 24px; }
`;

const pulse = keyframes`
  0%, 100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(47, 111, 237, 0.35); }
  50% { transform: scale(1.06); box-shadow: 0 0 0 8px rgba(47, 111, 237, 0); }
`;

const pulseYellow = keyframes`
  0%, 100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(214, 158, 46, 0.42); opacity: 1; }
  50% { transform: scale(1.06); box-shadow: 0 0 0 10px rgba(214, 158, 46, 0); opacity: 0.88; }
`;

const COLORS = {
  completed: "#22A06B",
  current: "#2F6FED",
  bench: "#D69E2E",
  pending: "#C8CDD5",
};

const getNodeTooltip = (step) => {
  if (step.status === "completed") {
    return `Approved by ${step.name}`;
  }

  if (step.status === "current") {
    return `Waiting for ${step.name}`;
  }

  if (step.status === "bench") {
    return `Bench approved: ${step.name}`;
  }

  return `Pending ${step.name} approval`;
};

const Segment = ({ status, isVertical, prefersReducedMotion }) => {
  if (status === "completed") {
    return (
      <Box
        flex={1}
        minW={isVertical ? "4px" : "120px"}
        minH={isVertical ? "120px" : "4px"}
        h={isVertical ? "100%" : "4px"}
        w={isVertical ? "4px" : "100%"}
        bg={COLORS.completed}
        borderRadius="full"
      />
    );
  }

  if (status === "current") {
    return (
      <Box
        flex={1}
        minW={isVertical ? "4px" : "120px"}
        minH={isVertical ? "120px" : "4px"}
        h={isVertical ? "100%" : "4px"}
        w={isVertical ? "4px" : "100%"}
        borderRadius="full"
        bg={
          isVertical
            ? "repeating-linear-gradient(180deg, #2F6FED 0px, #2F6FED 6px, transparent 6px, transparent 12px)"
            : "repeating-linear-gradient(90deg, #2F6FED 0px, #2F6FED 6px, transparent 6px, transparent 12px)"
        }
        backgroundColor="#E6EEFF"
        backgroundSize={isVertical ? "4px 24px" : "24px 4px"}
        animation={
          prefersReducedMotion
            ? "none"
            : `${isVertical ? arrowFlowVertical : arrowFlowHorizontal} 1.2s linear infinite`
        }
      />
    );
  }

  if (status === "bench") {
    return (
      <Box
        flex={1}
        minW={isVertical ? "4px" : "120px"}
        minH={isVertical ? "120px" : "4px"}
        h={isVertical ? "100%" : "4px"}
        w={isVertical ? "4px" : "100%"}
        borderRadius="full"
        bg={COLORS.bench}
        animation={
          prefersReducedMotion
            ? "none"
            : `${pulseYellow} 1.2s ease-in-out infinite`
        }
      />
    );
  }

  return (
    <Box
      flex={1}
      minW={isVertical ? "4px" : "120px"}
      minH={isVertical ? "120px" : "4px"}
      h={isVertical ? "100%" : "4px"}
      w={isVertical ? "4px" : "100%"}
      borderRadius="full"
      bg={
        isVertical
          ? "repeating-linear-gradient(180deg, #C8CDD5 0px, #C8CDD5 8px, transparent 8px, transparent 14px)"
          : "repeating-linear-gradient(90deg, #C8CDD5 0px, #C8CDD5 8px, transparent 8px, transparent 14px)"
      }
    />
  );
};

const statusMeta = {
  completed: { label: "Approved", colorScheme: "green" },
  current: { label: "In Progress", colorScheme: "blue" },
  bench: { label: "Bench", colorScheme: "yellow" },
  pending: { label: "Pending", colorScheme: "gray" },
};

const Node = ({ step, prefersReducedMotion, isVertical, showLabel = true }) => {
  const pendingBg = useColorModeValue("white", "gray.700");
  const pendingBorder = useColorModeValue("#C8CDD5", "#A0AEC0");

  const isCompleted = step.status === "completed";
  const isCurrent = step.status === "current";
  const isBench = step.status === "bench";

  return (
    <Tooltip label={getNodeTooltip(step)} hasArrow openDelay={200}>
      <VStack spacing={2} minW="40px" align="center">
        <Circle
          size="40px"
          bg={
            isCompleted
              ? COLORS.completed
              : isCurrent
                ? COLORS.current
                : isBench
                  ? COLORS.bench
                  : pendingBg
          }
          border={isCompleted || isCurrent || isBench ? "none" : "2px solid"}
          borderColor={
            isCompleted || isCurrent || isBench ? "transparent" : pendingBorder
          }
          color={isCompleted || isCurrent || isBench ? "white" : "gray.500"}
          animation={
            (isCurrent || isBench) && !prefersReducedMotion
              ? `${isBench ? pulseYellow : pulse} 1.4s ease-in-out infinite`
              : "none"
          }
          aria-label={getNodeTooltip(step)}
        >
          {isCompleted ? (
            <Box as={BsCheck} boxSize={5} />
          ) : isBench ? (
            <Box as={BsLightningChargeFill} boxSize={4} />
          ) : (
            <Box boxSize={2} borderRadius="full" bg="currentColor" />
          )}
        </Circle>

        {showLabel && (
          <Text
            fontSize="xs"
            fontWeight={isCurrent ? "700" : "500"}
            color={
              isCompleted
                ? "green.700"
                : isCurrent
                  ? "blue.700"
                  : isBench
                    ? "yellow.700"
                    : "gray.600"
            }
            textAlign="center"
            maxW={isVertical ? "180px" : "100px"}
            lineHeight="1.2"
            whiteSpace="normal"
            wordBreak="break-word"
          >
            {step.name}
          </Text>
        )}
      </VStack>
    </Tooltip>
  );
};

const ApprovalProgressBar = ({ steps = [] }) => {
  const isMobile = useBreakpointValue({ base: true, md: false });
  const prefersReducedMotion = usePrefersReducedMotion();

  if (!steps || steps.length <= 1) {
    return null;
  }

  if (isMobile) {
    return (
      <VStack align="stretch" spacing={0}>
        {steps.map((step, index) => {
          const hasNext = index < steps.length - 1;
          const meta = statusMeta[step.status] || statusMeta.pending;

          return (
            <Flex
              key={step.id || `${step.name}-${index}`}
              align="flex-start"
              borderRadius="md"
              px={1}
            >
              <VStack
                w="44px"
                spacing={0}
                align="center"
                flexShrink={0}
                h="100%"
              >
                <Node
                  step={step}
                  prefersReducedMotion={prefersReducedMotion}
                  isVertical
                  showLabel={false}
                />
                {hasNext && (
                  <Box flex={1} minH="40px" py={1}>
                    <Segment
                      status={step.status}
                      isVertical
                      prefersReducedMotion={prefersReducedMotion}
                    />
                  </Box>
                )}
              </VStack>
              <Flex align="center" pl={3} minW={0} flex={1} pt={1}>
                <Box minW={0}>
                  <HStack spacing={2} mb={1} align="center">
                    <Text
                      fontSize="sm"
                      fontWeight="semibold"
                      color="gray.700"
                      noOfLines={3}
                    >
                      {step.name}
                    </Text>
                    <Badge colorScheme={meta.colorScheme} fontSize="2xs">
                      {meta.label}
                    </Badge>
                  </HStack>
                  <Text fontSize="xs" color="gray.600" whiteSpace="normal">
                    {getNodeTooltip(step)}
                  </Text>
                </Box>
              </Flex>
            </Flex>
          );
        })}
      </VStack>
    );
  }

  return (
    <HStack align="flex-start" spacing={0} w="100%" overflowX="auto" pb={1}>
      {steps.map((step, index) => {
        const hasNext = index < steps.length - 1;

        return (
          <React.Fragment key={step.id || `${step.name}-${index}`}>
            <Node step={step} prefersReducedMotion={prefersReducedMotion} />
            {hasNext && (
              <Flex flex={1} align="center" px={2} pt="18px" minW="120px">
                <Segment
                  status={step.status}
                  prefersReducedMotion={prefersReducedMotion}
                />
              </Flex>
            )}
          </React.Fragment>
        );
      })}
    </HStack>
  );
};

export default memo(ApprovalProgressBar);
